import { AlertDialog, Button, Flex } from "@radix-ui/themes";
import { useState } from "react";
import {
  useAgencyPointsConvertQuote,
  useConvertAgencyPointsToCoins,
} from "../../../../hooks/agency-points-convert/use-agency-points-convert";
import { hasMessage, t } from "../../../../i18n";
import { AgencyPointsConvertQuote } from "../../../../services/agency-points-convert/types";

// Same rounding as the server (convertAgencyPointsToCoins): points cost with 2
// decimals. The server recomputes it; this is only the preview.
const pointsCost = (coins: number, quote: AgencyPointsConvertQuote) =>
  Math.round(((coins * quote.ratePoints) / quote.rateCoins) * 100) / 100;

const fmt = (value: number, decimals = 0) =>
  value.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const errorMessage = (code: string | null | undefined, min: number) => {
  const key = `convert.error.${code}`;
  return hasMessage(key) ? t(key, { min: fmt(min) }) : t("convert.error.generic");
};

const card = {
  background: "#fff",
  padding: "16px",
  borderRadius: "10px",
  marginBottom: "12px",
};

export const ConvertPoints = () => {
  const { quote, error: quoteError, isLoading } = useAgencyPointsConvertQuote();
  const { convert, isLoading: isConverting } = useConvertAgencyPointsToCoins();

  const [amount, setAmount] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  if (isLoading) {
    return <div style={{ padding: 20, color: "#777" }}>{t("convert.loading")}</div>;
  }

  if (!quote) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ ...card, color: "#b71c1c" }}>
          {errorMessage(quoteError, 0)}
        </div>
      </div>
    );
  }

  const coins = amount ? Number(amount) : 0;
  const cost = coins > 0 ? pointsCost(coins, quote) : 0;

  let validation: string | null = null;
  if (amount && cost > quote.points) {
    validation = errorMessage("not_enough_points", quote.minCoins);
  }

  const canConvert = Boolean(amount) && !validation && !isConverting;

  // Ready-made packages only (owner, 05/11/2026): whole multiples of the minimum
  // (38,500 coins = 57,500 points), so the points cost never has decimals. The
  // larger ones match the packages in the rate table.
  const packages = [1, 10, 50, 100, 500, 1000, 5000].map((times) => {
    const packageCoins = quote.minCoins * times;
    return { coins: packageCoins, points: pointsCost(packageCoins, quote) };
  });

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setFeedback(null);
    try {
      const result = await convert({ quantityCoins: coins });
      if (result?.status === "success" && result.data) {
        setFeedback({
          type: "success",
          message: t("convert.success", {
            points: fmt(result.data.pointsSpent, Number.isInteger(result.data.pointsSpent) ? 0 : 2),
            coins: fmt(result.data.quantityCoins),
          }),
        });
        setAmount("");
        return;
      }
      setFeedback({ type: "error", message: errorMessage(result?.error, quote.minCoins) });
    } catch {
      setFeedback({ type: "error", message: t("convert.error.generic") });
    }
  };

  const row = (label: string, value: string) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "6px 0",
        borderBottom: "1px solid #eee",
        fontSize: 14,
      }}
    >
      <span style={{ color: "#666" }}>{label}</span>
      <strong style={{ textAlign: "right" }}>{value}</strong>
    </div>
  );

  return (
    <div style={{ background: "#f0f3fc", padding: "12px" }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div
          style={{
            flex: 1,
            background: "linear-gradient(90deg, #5c6bc0, #3f51b5)",
            borderRadius: 10,
            padding: "16px 12px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: "bold" }}>{fmt(quote.points, 2)}</div>
          <div style={{ fontSize: 13 }}>{t("convert.pointsAvailable")}</div>
        </div>
        <div
          style={{
            flex: 1,
            background: "linear-gradient(90deg, #fca91d, #ff7b00)",
            borderRadius: 10,
            padding: "16px 12px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: "bold" }}>{fmt(quote.coinsForSale)}</div>
          <div
            style={{
              fontSize: 13,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 5,
            }}
          >
            {t("convert.coinsStock")}
            <img src={"/coin.png"} style={{ width: 16 }} />
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 13, color: "#777", marginBottom: 4 }}>
          {t("convert.rateTitle")}
        </div>
        <div style={{ fontSize: 16, fontWeight: "bold" }}>
          {t("convert.rateLine", {
            points: fmt(quote.ratePoints),
            coins: fmt(quote.rateCoins),
          })}
        </div>
        <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>
          {t("convert.ratePerCoin", { perCoin: fmt(quote.pointsPerCoin, 4) })}
        </div>
      </div>

      <div style={card}>
        <label style={{ fontSize: 14, fontWeight: "bold" }}>
          {t("convert.packagesLabel")}
        </label>

        <div style={{ display: "grid", gap: 8, marginTop: 8, marginBottom: 10 }}>
          {packages.map((pkg) => {
            const affordable = pkg.points <= quote.points;
            const selected = coins === pkg.coins;
            return (
              <button
                key={pkg.coins}
                disabled={!affordable}
                aria-pressed={selected}
                onClick={() => {
                  setFeedback(null);
                  setAmount(String(pkg.coins));
                }}
                style={{
                  ...chip,
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 14,
                  textAlign: "left",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  background: selected ? "#3f51b5" : "#fff",
                  color: selected ? "#fff" : affordable ? "#3f51b5" : "#9e9e9e",
                  borderColor: affordable ? "#3f51b5" : "#ddd",
                  cursor: affordable ? "pointer" : "default",
                }}
              >
                <span>
                  {t("convert.packageLine", { points: fmt(pkg.points), coins: fmt(pkg.coins) })}
                </span>
                {!affordable && <span style={{ fontSize: 12 }}>{t("convert.packageNotEnough")}</span>}
              </button>
            );
          })}
        </div>

        {validation && (
          <div style={{ fontSize: 14, color: "#c62828" }}>{validation}</div>
        )}
        <small style={{ display: "block", color: "#777", marginTop: 6 }}>
          {t("convert.packagesHint", { min: fmt(quote.minCoins) })}
        </small>

        {feedback && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 15,
              padding: "12px 14px",
              borderRadius: 8,
              borderLeft: `4px solid ${feedback.type === "success" ? "#2e7d32" : "#c62828"}`,
              background: feedback.type === "success" ? "#edf7ed" : "#fdecea",
              color: feedback.type === "success" ? "#1b5e20" : "#b71c1c",
              fontSize: 14,
              lineHeight: 1.45,
            }}
          >
            {feedback.message}
          </div>
        )}

        <button
          disabled={!canConvert}
          onClick={() => setConfirmOpen(true)}
          style={{
            background: canConvert ? "#3f51b5" : "#bbbaba",
            width: "100%",
            color: "#fff",
            border: "none",
            cursor: canConvert ? "pointer" : "default",
            padding: 12,
            fontSize: 16,
            borderRadius: 8,
            marginTop: 20,
          }}
        >
          {isConverting ? t("convert.sending") : t("convert.button")}
        </button>
      </div>

      <AlertDialog.Root open={confirmOpen}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>{t("convert.confirmTitle")}</AlertDialog.Title>
          <div style={{ marginTop: 8 }}>
            {row(t("convert.confirmCoins"), t("convert.coinsUnit", { value: fmt(coins) }))}
            {row(t("convert.confirmPoints"), t("convert.pointsUnit", { value: fmt(cost) }))}
            {row(
              t("convert.confirmRate"),
              t("convert.rateLine", {
                points: fmt(quote.ratePoints),
                coins: fmt(quote.rateCoins),
              })
            )}
            {row(t("convert.confirmPointsAfter"), fmt(quote.points - cost, 2))}
            {row(t("convert.confirmCoinsAfter"), fmt(quote.coinsForSale + coins))}
          </div>
          <Flex gap="3" mt="4" justify="end">
            <Button variant="soft" color="gray" onClick={() => setConfirmOpen(false)}>
              {t("convert.cancel")}
            </Button>
            <Button onClick={handleConfirm}>{t("convert.confirm")}</Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </div>
  );
};

const chip = {
  padding: "6px 12px",
  borderRadius: 16,
  border: "1px solid #3f51b5",
  background: "#fff",
  color: "#3f51b5",
  fontSize: 13,
  cursor: "pointer",
} as const;
